import React, {useEffect, useState} from "react"
// MUI
import {Box, Typography, Button, Avatar, Stack, Skeleton} from "@mui/material"
import {AddRounded, ArrowForwardRounded, InsightsRounded} from "@mui/icons-material"
// Context
import {useData} from "@/context/PackagingDataProvider"
// Translation
import {useTranslation} from "react-i18next"
// Layout
import {PageHeader, Badge, PackagingSection} from "@/components/Layout"
import {BigNumber} from "@/components/BigNumber"
import GettingStarted from "./GettingStarted"
import {PackagingStep} from "@/constants/packagingSteps"
import {packagingTokens as tk} from "@/theme/packagingTokens"
import {actionTone, formatAction, formatEntityType, relativeTime, initialsFor, avatarColor} from "./activityUtils"

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type DashboardViewProps = {
    // Getting-started tracker: a step was clicked — navigate + spotlight.
    onGuideStep?: (step: PackagingStep) => void
    orgName: string
    onNavigate: (section: PackagingSection) => void
}

// ─── Recent team-activity preview (real packaging audit logs, top 5) ───
function ActivityPreview(props: {onViewAll: () => void; orgFilter: string}) {
    const {onViewAll, orgFilter} = props
    const {t} = useTranslation(["common"])
    const {getPackagingActivityLogs} = useData()
    const [logs, setLogs] = useState<any[] | null>(null)

    useEffect(() => {
        let alive = true
        setLogs(null)
        getPackagingActivityLogs(1, 5, undefined, orgFilter || undefined).then((res: any) => {
            if (alive) setLogs(res?.events ?? [])
        })
        return () => {alive = false}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgFilter])

    return (
        <Box sx={{mt: 4}}>
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5}}>
                <Typography sx={{fontSize: "16px", fontWeight: 700}}>{t("nav_team_activity")}</Typography>
                <Button onClick={onViewAll} endIcon={<ArrowForwardRounded sx={{fontSize: 16}} />}
                        sx={{textTransform: "none", fontSize: "13px", fontWeight: 500, color: tk.color.textSecondary, "&:hover": {backgroundColor: tk.color.bg}}}>
                    {t("view_all")}
                </Button>
            </Box>

            <Box sx={{backgroundColor: tk.color.surface, border: `1px solid ${tk.color.border}`, borderRadius: tk.radius.card, overflow: "hidden"}}>
                {logs === null ? (
                    Array.from({length: 3}).map((_, i) => (
                        <Box key={i} sx={{p: 2, borderBottom: i < 2 ? `1px solid ${tk.color.borderLight}` : "none"}}>
                            <Skeleton variant={"rounded"} height={28} />
                        </Box>
                    ))
                ) : logs.length === 0 ? (
                    <Box sx={{py: 6, textAlign: "center"}}>
                        <InsightsRounded sx={{fontSize: 28, color: tk.color.textTertiary, mb: 1}} />
                        <Typography sx={{fontSize: "13.5px", color: tk.color.textSecondary}}>{t("activity_empty_title")}</Typography>
                    </Box>
                ) : (
                    logs.map((log: any, i: number) => (
                        <Box key={i} sx={{display: "flex", alignItems: "center", gap: 1.5, p: 2, borderBottom: i < logs.length - 1 ? `1px solid ${tk.color.borderLight}` : "none"}}>
                            <Avatar sx={{width: 32, height: 32, fontSize: "12px", fontWeight: 700, bgcolor: avatarColor(log.user_email || log.user_uuid)}}>
                                {initialsFor(log.user_email || log.user_uuid)}
                            </Avatar>
                            <Box sx={{minWidth: 0, flex: 1}}>
                                <Typography sx={{fontSize: "13.5px", color: tk.color.textPrimary, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}>
                                    <Box component={"strong"}>{log.user_email || log.user_uuid || "—"}</Box>
                                    {" "}{formatAction(log.action).toLowerCase()}{" "}
                                    {formatEntityType(log.entity_type).toLowerCase()}
                                    {log.entity_label ? ` · ${log.entity_label}` : ""}
                                </Typography>
                                <Typography sx={{fontSize: "12px", color: tk.color.textTertiary, mt: 0.25}}>
                                    {relativeTime(log["@timestamp"])}
                                </Typography>
                            </Box>
                            <Badge tone={actionTone(log.action)}>{formatAction(log.action)}</Badge>
                        </Box>
                    ))
                )}
            </Box>
        </Box>
    )
}

export default function DashboardView(props: DashboardViewProps) {
    const {orgName, onNavigate, onGuideStep} = props
    const {t} = useTranslation(["common"])
    const {products, totalProducts, brands, getPackagingScans, commonFilters} = useData()

    const productsCount = totalProducts ?? (products ? products.length : undefined)
    const brandsCount = brands ? brands.length : undefined

    // Total packaging scans (Standard + GS1) over the last 30 days.
    const [scans, setScans] = useState<{count: number; comparison: number | null} | null | undefined>(undefined)
    const orgFilter = commonFilters?.organisation ?? ""
    useEffect(() => {
        let alive = true
        setScans(undefined)
        getPackagingScans(30, orgFilter).then((res: any) => {
            if (alive) setScans(res ?? null)
        })
        return () => {alive = false}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orgFilter])

    // Tiles hug their content (BigNumber no longer forces a height).
    const tileSx = {}

    // Spreadsheet export moved to the Batch management page (Tools).

    return (
        <Box>
            <PageHeader
                title={t("packaging_dashboard")}
                subtitle={
                    <>
                        {t("overview_for")}{" "}
                        <Box component={"strong"} sx={{color: tk.color.textPrimary}}>{orgName}</Box>
                    </>
                }
                actions={
                    <>
                        <Button variant={"contained"} disableElevation onClick={() => onNavigate("products")} startIcon={<AddRounded sx={{fontSize: 16}} />}
                                sx={{textTransform: "none", fontSize: "13.5px", fontWeight: 600, borderRadius: tk.radius.sm, px: 2}}>
                            {t("new_product")}
                        </Button>
                    </>
                }
            />

            {/* Stats — unified BigNumber tiles (shared with the dashboard app) */}
            <Box data-step-target={"dashboard-stats"} sx={{display: "grid", gridTemplateColumns: {xs: "1fr", sm: "1fr 1fr", lg: "repeat(3, 1fr)"}, gap: 2, alignItems: "start"}}>
                <BigNumber label={t("stat_total_products")} value={productsCount !== undefined ? productsCount.toLocaleString() : "—"} sx={tileSx} />
                <BigNumber label={t("stat_active_brands")} value={brandsCount !== undefined ? brandsCount.toLocaleString() : "—"} sx={tileSx} />
                <BigNumber
                    label={t("stat_total_scans")}
                    value={scans === undefined ? "…" : scans === null ? "—" : scans.count.toLocaleString()}
                    comparison={scans && scans.count !== undefined ? scans.comparison : undefined}
                    sx={tileSx}
                />
            </Box>

            {/* Getting-started game — between the stats and the activity feed */}
            {onGuideStep && <GettingStarted onGuideStep={onGuideStep} />}

            <ActivityPreview onViewAll={() => onNavigate("activity")} orgFilter={orgFilter} />

        </Box>
    )
}
